import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';
import { ChartTooltipCard, ChartTooltipRow, EmptyState, Panel } from './Panel';
import { StreakBin } from './metrics';
import { accent, chart, formatCount, series } from './tokens';

interface StreakProgressChartProps {
  data: StreakBin[];
  streakTarget: number;
  enabled: boolean;
}

/**
 * How many customers sit at each rung of the streak ladder. The rung that
 * equals the target is highlighted: those customers are one visit from a
 * reward, which is the only bar that implies an action.
 */
export const StreakProgressChart: React.FC<StreakProgressChartProps> = ({
  data,
  streakTarget,
  enabled
}) => {
  const total = data.reduce((sum, bin) => sum + bin.customers, 0);
  const nearlyThere = data.find((bin) => bin.streak === streakTarget - 1)?.customers ?? 0;

  const renderTooltip = ({ active, label }: TooltipProps<number, string>) => {
    if (!active) return null;
    const bin = data.find((entry) => entry.streak === label);
    if (!bin) return null;
    return (
      <ChartTooltipCard title={`${bin.streak} in a row`}>
        <ChartTooltipRow
          color={bin.streak >= streakTarget ? accent.deep : series.stamps}
          label='Customers'
          value={formatCount(bin.customers)}
        />
      </ChartTooltipCard>
    );
  };

  if (!enabled) {
    return (
      <Panel
        title='Streak progress'
        subtitle='Streaks are switched off for this location'
        hint='Turn streaks on from the Details tab to reward customers for visiting on consecutive days.'
      >
        <EmptyState
          height={chart.height}
          message='Streaks are off, so nobody is building one. You can switch them on from the Details tab.'
        />
      </Panel>
    );
  }

  return (
    <Panel
      title='Streak progress'
      subtitle={
        total
          ? `${formatCount(total)} on a live streak${
              streakTarget > 1 ? ` · ${formatCount(nearlyThere)} one visit from a reward` : ''
            }`
          : `Target is ${streakTarget} in a row`
      }
      hint='Customers with a streak running right now, grouped by how many consecutive qualifying visits they have made. Customers on no streak are left out.'
    >
      {!total ? (
        <EmptyState
          height={chart.height}
          message='Nobody has a streak running right now. Streaks start on a customer’s next visit.'
        />
      ) : (
        <ResponsiveContainer width='100%' height={chart.height}>
          <BarChart data={data} margin={chart.margin}>
            <CartesianGrid vertical={false} stroke={chart.gridStroke} />
            <XAxis
              dataKey='streak'
              tick={chart.tick}
              tickLine={false}
              axisLine={{ stroke: chart.gridStroke }}
            />
            <YAxis
              tick={chart.tick}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={44}
            />
            <Tooltip content={renderTooltip} cursor={{ fill: chart.gridStroke, opacity: 0.7 }} />
            <Bar dataKey='customers' radius={[3, 3, 0, 0]} maxBarSize={38}>
              {data.map((bin) => (
                <Cell
                  key={bin.streak}
                  fill={bin.streak >= streakTarget - 1 ? accent.deep : series.stamps}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

export default StreakProgressChart;
